<template>
  <div>
    <HeaderContent :title="isEdit ? 'Edit Package' : 'Create New Package'" />

    <!-- Modals (Imported UX from Offer Document) -->
    <TransportationForm
      v-if="modals.transportation"
      :modal="modals.transportation"
      :action="selectedItem.index !== undefined ? 'edit' : 'add'"
      :selectedData="selectedItem"
      :defaultPax="form.pax"
      :totalDays="form.programs.length"
      :currentDayIndex="activeDayIndex"
      @update:modal="(val) => modals.transportation = val"
      @addOrUpdate="handleSaveTransportation"
    />

    <AccommodationForm
      v-if="modals.accommodation"
      :modal="modals.accommodation"
      :selectedData="selectedItem"
      :defaultPax="form.pax"
      @update:modal="(val) => modals.accommodation = val"
      @addOrUpdate="handleSaveItem('accommodations', $event)"
    />

    <ActivityForm
      v-if="modals.activity"
      :modal="modals.activity"
      :selectedData="selectedItem"
      :defaultPax="form.pax"
      @update:modal="(val) => modals.activity = val"
      @addOrUpdate="handleSaveItem('activities', $event)"
    />

    <MealForm
      v-if="modals.meals"
      :modal="modals.meals"
      :selectedData="selectedItem"
      :defaultPax="form.pax"
      @update:modal="(val) => modals.meals = val"
      @addOrUpdate="handleSaveItem('meals', $event)"
    />

    <PackagePreview
      v-if="modals.preview"
      :modal="modals.preview"
      :form="form"
      :destinations="destinations"
      :isEdit="isEdit"
      @update:modal="(val) => modals.preview = val"
      @confirm="submitPackage"
    />

    <BForm @submit.stop.prevent>
    <BRow>
      <BCol cols="12">
        <!-- 1. PACKAGE BASIC INFO -->
        <BCard class="mb-4 shadow-sm border-0">
            <template #header>
                <h5 class="mb-0 font-weight-bold text-primary text-left">1. Package Information</h5>
            </template>
            <div class="p-2">
                <BRow>
                    <BCol lg="6" class="text-left">
                        <BFormGroup label="Package Name *" label-for="name">
                            <BFormInput 
                                v-model="form.name" 
                                id="name"
                                placeholder="e.g. Medan - Lake Toba 4D3N" 
                                :class="{ 'is-invalid': v$.form.name.$dirty && v$.form.name.$invalid }"
                            />
                            <BFormInvalidFeedback :state="!(v$.form.name.$dirty && v$.form.name.$invalid)">
                                {{ v$.form.name.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                    <BCol lg="3" class="text-left">
                        <BFormGroup label="Pax *" label-for="pax">
                            <BFormInput 
                                type="number" 
                                id="pax"
                                v-model="form.pax" 
                                min="1" 
                                :class="{ 'is-invalid': v$.form.pax.$dirty && v$.form.pax.$invalid }"
                            />
                            <BFormInvalidFeedback :state="!(v$.form.pax.$dirty && v$.form.pax.$invalid)">
                                {{ v$.form.pax.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                    <BCol lg="3" class="text-left">
                        <BFormGroup label="Duration (Days) *" label-for="duration">
                            <BFormInput 
                                type="number" 
                                id="duration"
                                :model-value="form.programs.length"
                                disabled
                                :class="{ 'is-invalid': v$.form.duration_days.$dirty && v$.form.duration_days.$invalid }"
                            />
                            <BFormInvalidFeedback :state="!(v$.form.duration_days.$dirty && v$.form.duration_days.$invalid)">
                                {{ v$.form.duration_days.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>
            </div>
        </BCard>

        <!-- 2. ITINERARY SECTION -->
        <BCard class="mb-4 shadow-sm border-0">
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0 font-weight-bold text-primary">2. Program Description</h5>
                </div>
            </template>
            <div class="p-2">
                <div v-if="form.programs.length === 0" class="text-center py-5 text-muted italic">
                    <p>Current program is empty. Click "Add Day" to start.</p>
                </div>
                
                <div v-for="(program, index) in form.programs" :key="index" class="card shadow-sm mb-4 border-left-primary">
                    <div class="card-header bg-light py-2">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0 font-weight-bold text-left">Day {{ program.day }}</h6>
                            <BButton variant="link" class="text-danger p-0" title="Remove Day" @click="removeDay(index)">
                                <i class="fas fa-trash"></i>
                            </BButton>
                        </div>
                    </div>
                    <div class="card-body">
                        <BRow class="text-left">
                            <BCol md="4">
                                <BFormGroup label="From Destination *">
                                    <v-select 
                                        v-model="program.from_destination_uuid" 
                                        :options="destinations" 
                                        :reduce="d => d.uuid" 
                                        label="name"
                                        placeholder="Pick Location"
                                        :class="{ 'is-invalid-select': submitted && !program.from_destination_uuid }"
                                        style="background-color: white"
                                        :loading="loadingStates.destinations"
                                        @open="loadDestinations"
                                    />
                                    <small v-if="submitted && !program.from_destination_uuid" class="text-danger">Destination is required</small>
                                </BFormGroup>
                            </BCol>
                            <BCol md="4">
                                <BFormGroup label="To Destination *">
                                    <v-select 
                                        v-model="program.to_destination_uuid" 
                                        :options="destinations" 
                                        :reduce="d => d.uuid" 
                                        label="name"
                                        placeholder="Pick Location"
                                        :class="{ 'is-invalid-select': submitted && !program.to_destination_uuid }"
                                        style="background-color: white"
                                        :loading="loadingStates.destinations"
                                        @open="loadDestinations"
                                    />
                                    <small v-if="submitted && !program.to_destination_uuid" class="text-danger">Destination is required</small>
                                </BFormGroup>
                            </BCol>
                            <BCol md="4">
                                <BFormGroup label="Program Name (Pick or Create)">
                                    <v-select
                                        v-model="program.name"
                                        :options="getFilteredPrograms(program)"
                                        label="name"
                                        :reduce="p => p && typeof p === 'object' ? p.name : p"
                                        taggable
                                        :disabled="!program.from_destination_uuid || !program.to_destination_uuid"
                                        @option:selected="(val) => selectProgramForDay(index, val)"
                                        placeholder="e.g. Airport Transfer & Hotel Check-in"
                                        style="background-color: white"
                                        :loading="loadingStates.masterPrograms"
                                        @open="loadMasterPrograms"
                                    >
                                        <template #option="option">
                                            <div class="d-flex flex-column">
                                                <div class="d-flex justify-content-between align-items-center text-left">
                                                    <span>{{ option.name }}</span>
                                                    <small class="badge bg-success px-2" v-if="option.uuid">
                                                        <i class="fas fa-plus me-1"></i> Add Program
                                                    </small>
                                                <small class="badge bg-success px-2" v-else>
                                                    <i class="fas fa-plus me-1"></i> Create Program
                                                </small>
                                            </div>
                                            <small>{{ option.pax || form.pax }} Pax</small>
                                            </div>
                                        </template>

                                        <template #no-options>
                                            <!-- <div v-if="searching" class="p-3 text-center">
                                                <div class="text-muted small mb-2">No program named "{{ search }}"</div>
                                                <div class="border rounded p-2 bg-light d-flex justify-content-between align-items-center">
                                                    <span class="text-dark font-weight-bold truncate-text">{{ search }}</span>
                                                    <span class="badge bg-success ms-2">
                                                        <i class="fas fa-magic me-1"></i> Create Program
                                                    </span>
                                                </div>
                                                <div class="mt-2 x-small text-muted italic">Press Enter to confirm</div>
                                            </div> -->
                                            <div class="p-3 text-muted small text-center">
                                                <i class="fas fa-search me-1"></i> Type to search or create program...
                                            </div>
                                        </template>
                                    </v-select>
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <!-- Sub-tabs for Day Items (The "Offer Document" Way) -->
                        <div class="mt-3">
                            <BTabs content-class="mt-3" active-nav-item-class="font-weight-bold">
                                <!-- Transportation Tab -->
                                <BTab active>
                                    <template #title>
                                        <i class="fas fa-car me-1"></i> Transportation
                                    </template>
                                    <div class="d-flex justify-content-end">
                                        <BButton variant="outline-primary" size="sm" class="mb-2" @click="openModalAdd('transportation', index)">
                                            <i class="fas fa-plus"></i> Add Transportation
                                        </BButton>
                                    </div>
                                    <table class="table table-sm table-bordered table-hover mb-0">
                                        <thead class="bg-light x-small">
                                            <tr><th>Vehicle</th><th>Pax</th><th>Price</th><th class="text-right">Subtotal</th><th width="80">Action</th></tr>
                                        </thead>
                                        <tbody class="small">
                                            <tr v-for="(item, idx) in program.transportations" :key="idx">
                                                <td class="text-left font-weight-bold">{{ item.name }}</td>
                                                <td>{{ item.pax }}</td>
                                                <td class="text-right">{{ formatRupiah(item.price) }}</td>
                                                <td class="text-right">{{ formatRupiah(item.sub_total || (item.price * item.pax)) }}</td>
                                                <td class="text-center">
                                                    <BButton variant="link" class="text-warning p-0 me-2" @click="openModalEdit({type: 'transportation', dayIndex: index, item: item, itemIndex: idx})"><i class="fas fa-edit"></i></BButton>
                                                    <BButton variant="link" class="text-danger p-0" @click="removeDayItem('transportations', index, idx)"><i class="fas fa-trash"></i></BButton>
                                                </td>
                                            </tr>
                                            <tr v-if="!program.transportations?.length"><td colspan="5" class="text-center text-muted italic p-3">No transportation added</td></tr>
                                        </tbody>
                                    </table>
                                </BTab>

                                <!-- Accommodation Tab -->
                                <BTab>
                                    <template #title>
                                        <i class="fas fa-hotel me-1"></i> Accommodation
                                    </template>
                                    <div class="d-flex justify-content-end">
                                        <BButton variant="outline-primary" size="sm" class="mb-2" @click="openModalAdd('accommodation', index)">
                                            <i class="fas fa-plus"></i> Add Accommodation
                                        </BButton>
                                    </div>
                                    <table class="table table-sm table-bordered table-hover mb-0">
                                        <thead class="bg-light x-small">
                                            <tr>
                                                <th>Hotel</th>
                                                <th>Room Type</th>
                                                <th class="text-center">Pax</th>
                                                <th class="text-right">Price</th>
                                                <th class="text-right">Subtotal</th>
                                                <th class="text-center">Extra Bed</th>
                                                <th class="text-right">Extra Bed Price</th>
                                                <th class="text-right">Total Price</th>
                                                <th width="80">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody class="small">
                                            <tr v-for="(item, idx) in program.accommodations" :key="idx">
                                                <td class="text-left font-weight-bold">{{ item.name }}</td>
                                                <td>{{ item.room_type }}</td>
                                                <td class="text-center">{{ item.pax }}</td>
                                                <td class="text-right">{{ formatRupiah(item.price) }}</td>
                                                <td class="text-right">{{ formatRupiah(item.sub_total || (item.price * item.pax)) }}</td>
                                                <td class="text-center">
                                                    <span 
                                                        class="badge" 
                                                        :class="item.extra_bed == 1 ? 'bg-success' : 'bg-danger'"
                                                    >
                                                        {{ item.extra_bed == 1 ? 'Yes' : 'No' }}
                                                    </span>
                                                </td>
                                                <td class="text-right">{{ formatRupiah(item.extra_bed_price) }}</td>
                                                <td class="text-right fw-bold">{{ formatRupiah(item.total_price || ((item.price * item.pax) + (item.extra_bed_price || 0))) }}</td>
                                                <td class="text-center">
                                                    <BButton variant="link" class="text-warning p-0 me-2" @click="openModalEdit({type: 'accommodation', dayIndex: index, item: item, itemIndex: idx})"><i class="fas fa-edit"></i></BButton>
                                                    <BButton variant="link" class="text-danger p-0" @click="removeDayItem('accommodations', index, idx)"><i class="fas fa-trash"></i></BButton>
                                                </td>
                                            </tr>
                                            <tr v-if="!program.accommodations?.length"><td colspan="9" class="text-center text-muted italic p-3">No hotel added</td></tr>
                                        </tbody>
                                    </table>
                                </BTab>

                                <!-- Activities Tab -->
                                <BTab>
                                    <template #title>
                                        <i class="fas fa-walking me-1"></i> Activities
                                    </template>
                                    <div class="d-flex justify-content-end">
                                        <BButton variant="outline-primary" size="sm" class="mb-2" @click="openModalAdd('activity', index)">
                                            <i class="fas fa-plus"></i> Add Activity
                                        </BButton>
                                    </div>
                                    <table class="table table-sm table-bordered table-hover mb-0">
                                        <thead class="bg-light x-small">
                                            <tr><th>Activity</th><th>Pax</th><th>Price</th><th class="text-right">Subtotal</th><th width="80">Action</th></tr>
                                        </thead>
                                        <tbody class="small">
                                            <tr v-for="(item, idx) in program.activities" :key="idx">
                                                <td class="text-left font-weight-bold">{{ item.name }}</td>
                                                <td>{{ item.pax }}</td>
                                                <td class="text-right">{{ formatRupiah(item.price) }}</td>
                                                <td class="text-right">{{ formatRupiah(item.sub_total || (item.price * item.pax)) }}</td>
                                                <td class="text-center">
                                                    <BButton variant="link" class="text-warning p-0 me-2" @click="openModalEdit({type: 'activity', dayIndex: index, item: item, itemIndex: idx})"><i class="fas fa-edit"></i></BButton>
                                                    <BButton variant="link" class="text-danger p-0" @click="removeDayItem('activities', index, idx)"><i class="fas fa-trash"></i></BButton>
                                                </td>
                                            </tr>
                                            <tr v-if="!program.activities?.length"><td colspan="5" class="text-center text-muted italic p-3">No activity added</td></tr>
                                        </tbody>
                                    </table>
                                </BTab>

                                <!-- Meals Tab -->
                                <BTab>
                                    <template #title>
                                        <i class="fas fa-utensils me-1"></i> Meals
                                    </template>
                                    <div class="d-flex justify-content-end">
                                        <BButton variant="outline-primary" size="sm" class="mb-2" @click="openModalAdd('meals', index)">
                                            <i class="fas fa-plus"></i> Add Meal
                                        </BButton>
                                    </div>
                                    <table class="table table-sm table-bordered table-hover mb-0">
                                        <thead class="bg-light x-small">
                                            <tr><th>Meal Name</th><th>Pax</th><th>Price</th><th class="text-right">Subtotal</th><th width="80">Action</th></tr>
                                        </thead>
                                        <tbody class="small">
                                            <tr v-for="(item, idx) in program.meals" :key="idx">
                                                <td class="text-left font-weight-bold">{{ item.name }}</td>
                                                <td>{{ item.pax || form.pax }}</td>
                                                <td class="text-right">{{ formatRupiah(item.price) }}</td>
                                                <td class="text-right">{{ formatRupiah(item.sub_total || (item.price * (item.pax || form.pax))) }}</td>
                                                <td class="text-center">
                                                    <BButton variant="link" class="text-warning p-0 me-2" @click="openModalEdit({type: 'meals', dayIndex: index, item: item, itemIndex: idx})"><i class="fas fa-edit"></i></BButton>
                                                    <BButton variant="link" class="text-danger p-0" @click="removeDayItem('meals', index, idx)"><i class="fas fa-trash"></i></BButton>
                                                </td>
                                            </tr>
                                            <tr v-if="!program.meals?.length"><td colspan="5" class="text-center text-muted italic p-3">No meal added</td></tr>
                                        </tbody>
                                    </table>
                                </BTab>
                            </BTabs>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-center">
                    <BButton variant="success" @click="addDay" size="sm">
                        <i class="fas fa-plus-circle me-1"></i> Add Day
                    </BButton>
                </div>
            </div>
        </BCard>

        <!-- 3. INCLUSIONS & EXCLUSIONS -->
        <BCard class="mb-4 shadow-sm border-0">
            <template #header>
                <h5 class="mb-0 font-weight-bold text-primary text-left">3. Pricing</h5>
            </template>
            <div class="p-2">
                <BRow>
                    <BCol md="7" class="text-left">
                        <BFormGroup label="Inclusions">
                            <BFormTextarea v-model="form.inclusion" placeholder="What's included in this package..." rows="6" />
                        </BFormGroup>
                        <BFormGroup label="Exclusions" class="mt-3">
                            <BFormTextarea v-model="form.exclusion" placeholder="What's NOT included..." rows="6" />
                        </BFormGroup>
                    </BCol>
                    <BCol md="5">
                        <div class="card bg-light border-0 p-4 shadow-none">
                            <h6 class="font-weight-bold mb-4 border-bottom pb-2 text-left">Total Price</h6>
                            <table class="table table-sm table-borderless mb-0">
                                <tr v-for="(prog, pidx) in form.programs" :key="'sum-'+pidx">
                                    <td class="text-left text-muted small">Day {{ prog.day }} Subtotal</td>
                                    <td class="text-right small">{{ formatRupiah(getDaySubtotal(prog)) }}</td>
                                </tr>
                                <tr class="h4 border-top">
                                    <td class="text-left pt-2 font-weight-bold text-primary">Grand Total</td>
                                    <td class="text-right pt-2 text-primary font-weight-bold">
                                        {{ formatRupiah(calculatedTotal) }}
                                    </td>
                                </tr>

                                <tr class="h6 border-top">
                                    <td class="text-left pt-4 font-weight-bold">Price per Person ({{ form.pax }} pax)</td>
                                    <td class="text-right pt-4 font-weight-bold">
                                        {{ formatRupiah(calculatedTotal / form.pax) }}
                                    </td>
                                </tr>
                            </table>
                            <p class="small text-muted italic mt-3 text-left">
                                <i class="fas fa-info-circle"></i> This price is automatically calculated from all items added above.
                            </p>
                        </div>
                    </BCol>
                </BRow>
            </div>
        </BCard>

        <div class="d-flex justify-content-end p-4 bg-white shadow-sm rounded mb-4">
             <BButton variant="primary" class="me-2 px-4" @click="validateAndPreview">
                <i class="fas fa-save me-2"></i> {{ isEdit ? 'Update Package' : 'Save Package' }}
            </BButton>
            <BButton variant="danger" @click="router.push('/packages')">
                <i class="fas fa-times me-1"></i> Cancel
            </BButton>
        </div>
      </BCol>
    </BRow>
    </BForm>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import HeaderContent from "@/components/HeaderContent.vue";
import { useAuthStore } from "@/store/auth.js";
import { useLoading } from "vue-loading-overlay";
import { useToast } from "vue-toastification";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, minValue } from "@vuelidate/validators";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormTextarea,
    BRow,
    BTab,
    BTabs,
} from "bootstrap-vue-next";

// Form Components (using Program modals for full feature set)
import TransportationForm from "./program/TransportationForm.vue";
import AccommodationForm from "./program/AccommodationForm.vue";
import ActivityForm from "./program/ActivityForm.vue";
import MealForm from "./program/MealForm.vue";
import PackagePreview from "./program/PackagePreview.vue";

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const $loading = useLoading();

const isEdit = computed(() => !!route.params.uuid);

// Form State
const form = reactive({
    name: '',
    pax: 1,
    duration_days: 0,
    inclusion: '',
    exclusion: '',
    programs: []
});

const rules = {
    form: {
        name: { required, minLength: minLength(3) },
        pax: { required, minValue: minValue(1) },
        duration_days: { required, minValue: minValue(1) }
    }
};

const v$ = useVuelidate(rules, { form });

// Masters Data
const destinations = ref([]);
const masterPrograms = ref([]);
const loadingStates = reactive({
    destinations: false,
    masterPrograms: false
});

// Modal Control
const modals = reactive({
    transportation: false,
    accommodation: false,
    activity: false,
    meals: false,
    preview: false
});
const activeDayIndex = ref(null);
const selectedItem = ref({});
const activeItemIndex = ref(null);
const submitted = ref(false);

// Methods
const loadDestinations = async () => {
    if (destinations.value.length > 0) return;
    loadingStates.destinations = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/destination`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        destinations.value = res.data.data;
    } finally {
        loadingStates.destinations = false;
    }
};

const loadMasterPrograms = async () => {
    if (masterPrograms.value.length > 0) return;
    loadingStates.masterPrograms = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/program`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        masterPrograms.value = res.data.data;
    } finally {
        loadingStates.masterPrograms = false;
    }
};

const getFilteredPrograms = (day) => {
    return masterPrograms.value.filter(p => {
        const matchRoute = p.from_destination_uuid === day.from_destination_uuid && 
                          p.to_destination_uuid === day.to_destination_uuid;

        // Use loose equality to handle string/number comparison
        const matchPax = p.pax == day.pax;
        return matchRoute && matchPax;
    });
};

const addDay = () => {
    const index = form.programs.length;
    form.programs.push({
        day: index + 1,
        name: '',
        pax: form.pax,
        from_destination_uuid: null,
        to_destination_uuid: null,
        transportations: [],
        accommodations: [],
        activities: [],
        meals: []
    });
};

const removeDay = (index) => {
    if (form.programs.length === 1) {
        // Reset instead of delete if it's the last one
        form.programs[0] = {
            day: 1,
            name: '',
            pax: form.pax,
            from_destination_uuid: null,
            to_destination_uuid: null,
            transportations: [],
            accommodations: [],
            activities: [],
            meals: []
        };
        return;
    }
    form.programs.splice(index, 1);
    form.programs.forEach((p, idx) => p.day = idx + 1);
};

// Auto duration
watch(() => form.programs.length, (newVal) => {
    form.duration_days = newVal;
});

// Watch for pax changes to update all programs
watch(() => form.pax, (newPax) => {
    if (newPax && form.programs.length > 0) {
        const paxNumber = Number(newPax);
        form.programs.forEach(program => {
            program.pax = paxNumber;
        });
    }
});

const selectProgramForDay = async (dayIndex, programNameOrObj) => {
    if (!programNameOrObj) return;

    // If it's a string from taggable, just set name
    if (typeof programNameOrObj === 'string') {
        form.programs[dayIndex].name = programNameOrObj;
        return;
    }
    
    // If it's an object from master, load its components
    const master = programNameOrObj;
    if (!master || !master.uuid) return;
    
    form.programs[dayIndex].name = master.name;
    
    // Fetch full master program details to get its sub-items
    const loader = $loading.show();
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/program/detail/${master.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        const details = res.data.data;
        
        // Debug: Check what data we're getting
        console.log('Program details from API:', details);
        console.log('Inclusion:', details.inclusion);
        console.log('Exclusion:', details.exclusion);
        
        // Populate items
        form.programs[dayIndex].transportations = details.transportation?.map(t => ({...t, uuid: null})) || [];
        form.programs[dayIndex].accommodations = details.accommodation?.map(a => ({...a, uuid: null})) || [];
        form.programs[dayIndex].activities = details.activity?.map(a => ({...a, uuid: null})) || [];
        form.programs[dayIndex].meals = details.meals?.map(m => ({...m, uuid: null})) || [];
        
        // Append inclusions - check line by line to avoid duplicates
        if (details.inclusion) {
            const existingInclusions = form.inclusion ? form.inclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
            const newInclusions = details.inclusion.split('\n').map(line => line.trim()).filter(line => line);
            
            newInclusions.forEach(newLine => {
                if (!existingInclusions.includes(newLine)) {
                    form.inclusion = (form.inclusion ? form.inclusion + '\n' : '') + newLine;
                    existingInclusions.push(newLine);
                }
            });
        }
        
        // Append exclusions - check line by line to avoid duplicates
        if (details.exclusion) {
            const existingExclusions = form.exclusion ? form.exclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
            const newExclusions = details.exclusion.split('\n').map(line => line.trim()).filter(line => line);
            
            newExclusions.forEach(newLine => {
                if (!existingExclusions.includes(newLine)) {
                    form.exclusion = (form.exclusion ? form.exclusion + '\n' : '') + newLine;
                    existingExclusions.push(newLine);
                }
            });
        }
        
    } catch (e) {
        console.error(e);
        toast.error("Failed to load master program components");
    } finally {
        loader.hide();
    }
};

const formatRupiah = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);
};

// Modal Operations
const openModalAdd = (type, dayIndex) => {
    activeDayIndex.value = dayIndex;
    selectedItem.value = {};
    activeItemIndex.value = null;
    modals[type] = true;
};

const openModalEdit = ({type, dayIndex, item, itemIndex}) => {
    activeDayIndex.value = dayIndex;
    selectedItem.value = JSON.parse(JSON.stringify(item));
    selectedItem.value.index = itemIndex; // Add index for the modal logic
    activeItemIndex.value = itemIndex;
    modals[type] = true;
};

const handleSaveItem = (type, payload) => {
    const day = form.programs[activeDayIndex.value];
    if (payload.index !== undefined) {
        day[type].splice(payload.index, 1, payload);
    } else {
        day[type].push(payload);
    }
    
    // Sync inclusions/exclusions - check line by line to avoid duplicates
    if (payload.inclusion) {
        const existingInclusions = form.inclusion ? form.inclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
        const newInclusions = payload.inclusion.split('\n').map(line => line.trim()).filter(line => line);
        
        newInclusions.forEach(newLine => {
            if (!existingInclusions.includes(newLine)) {
                form.inclusion = (form.inclusion ? form.inclusion + '\n' : '') + newLine;
                existingInclusions.push(newLine);
            }
        });
    }

    // Sync exclusions - check line by line to avoid duplicates
    if (payload.exclusion) {
        const existingExclusions = form.exclusion ? form.exclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
        const newExclusions = payload.exclusion.split('\n').map(line => line.trim()).filter(line => line);
        
        newExclusions.forEach(newLine => {
            if (!existingExclusions.includes(newLine)) {
                form.exclusion = (form.exclusion ? form.exclusion + '\n' : '') + newLine;
                existingExclusions.push(newLine);
            }
        });
    }
};

const handleSaveTransportation = (payload) => {
    if (payload.applyToAllDays) {
        // Apply to all days
        const transportData = { ...payload };
        delete transportData.applyToAllDays; // Remove flag
        delete transportData.index; // Remove index if exists
        
        form.programs.forEach((program) => {
            if (!program.transportations) {
                program.transportations = [];
            }
            program.transportations.push({ ...transportData });
        });
        
        // toast.success(`Transportation added to all ${form.programs.length} days`);
        
        // Sync inclusions/exclusions from first occurrence
        if (transportData.inclusion) {
            const existingInclusions = form.inclusion ? form.inclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
            const newInclusions = transportData.inclusion.split('\n').map(line => line.trim()).filter(line => line);
            
            newInclusions.forEach(newLine => {
                if (!existingInclusions.includes(newLine)) {
                    form.inclusion = (form.inclusion ? form.inclusion + '\n' : '') + newLine;
                    existingInclusions.push(newLine);
                }
            });
        }
        
        if (transportData.exclusion) {
            const existingExclusions = form.exclusion ? form.exclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
            const newExclusions = transportData.exclusion.split('\n').map(line => line.trim()).filter(line => line);
            
            newExclusions.forEach(newLine => {
                if (!existingExclusions.includes(newLine)) {
                    form.exclusion = (form.exclusion ? form.exclusion + '\n' : '') + newLine;
                    existingExclusions.push(newLine);
                }
            });
        }
    } else {
        // Add to current day only (use existing logic)
        handleSaveItem('transportations', payload);
    }
};

const removeDayItem = (type, dayIndex, itemIndex) => {
    form.programs[dayIndex][type].splice(itemIndex, 1);
};

// Calculations
const getDaySubtotal = (day) => {
    let total = 0;
    total += (day.transportations?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax) || 0), 0) || 0);
    total += (day.accommodations?.reduce((s, i) => s + Number(i.total_price || (i.price * i.pax) || 0), 0) || 0);
    total += (day.activities?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax) || 0), 0) || 0);
    total += (day.meals?.reduce((s, i) => s + Number(i.sub_total || (i.price * (i.pax || form.pax)) || 0), 0) || 0);
    return total;
};

const calculatedTotal = computed(() => {
    return form.programs.reduce((acc, day) => acc + getDaySubtotal(day), 0);
});

// Save Flow
const validateAndPreview = async () => {
    submitted.value = true;
    v$.value.$touch();
    
    // Check basic info
    if (v$.value.$invalid) {
        toast.error("Please fill all required fields correctly.");
        return;
    }

    // Check programs count
    if (form.programs.length === 0) {
        toast.error("Package must have at least one program day.");
        return;
    }

    // Check program details
    for (let i = 0; i < form.programs.length; i++) {
        const p = form.programs[i];
        if (!p.from_destination_uuid || !p.to_destination_uuid || !p.name) {
            toast.error(`Please complete Program on Day ${i + 1}`);
            return;
        }
    }
    
    // If all valid, open preview
    modals.preview = true;
};

const submitPackage = async () => {
    const loader = $loading.show();
    try {
        const payload = {
            ...form,
            program: form.programs
        };
        
        const url = isEdit.value ? `${process.env.VUE_APP_API_URL}/package/edit/${route.params.uuid}` : `${process.env.VUE_APP_API_URL}/package/add`;
        const method = isEdit.value ? 'put' : 'post';
        
        const res = await axios[method](url, payload, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        
        if (res.data.status) {
            toast.success(res.data.message);
            router.push('/packages');
        }
    } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message || "Failed to save package");
    } finally {
        loader.hide();
    }
};

const loadData = async () => {
    if (!isEdit.value) return;
    const loader = $loading.show();
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/package/detail/${route.params.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        const d = res.data.data;
        
        form.name = d.name;
        form.pax = d.pax;
        form.duration_days = d.duration_days;
        form.inclusion = d.inclusion || '';
        form.exclusion = d.exclusion || '';
        
        // Map programs and ensure arrays exist
        form.programs = d.programs.map(p => ({
            ...p,
            transportations: p.transportations || [],
            accommodations: p.accommodations || [],
            activities: p.activities || [],
            meals: p.meals || []
        }));
    } finally {
        loader.hide();
    }
};

onMounted(() => {
    loadData();
    loadDestinations();
    // Default 1 day if creating new
    if (!isEdit.value && form.programs.length === 0) {
        addDay();
    }
});

</script>

<style scoped>
.border-left-primary { border-left: 5px solid #007bff !important; }
.x-small { font-size: 0.75rem; }
.small { font-size: 0.85rem; }
.italic { font-style: italic; }
:deep(.is-invalid-select .vs__dropdown-toggle) {
    border-color: #dc3545 !important;
}
</style>
